import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import ContactForm from '../components/ContactForm';
import { Github, Linkedin, Instagram, MapPin, Mail, Phone } from 'lucide-react';
import { useTranslation } from '../contexts/LanguageContext';

const ContactPage: React.FC = () => {
  const { t } = useTranslation();
  const socialLinks = [
    { 
      icon: <Github size={24} />, 
      href: 'https://github.com/asmaessafi', 
      label: 'GitHub',
      color: 'hover:text-gray-800' 
    },
    { 
      icon: <Linkedin size={24} />, 
      href: 'https://www.linkedin.com/in/asma-essafi-a60a43331/', 
      label: 'LinkedIn',
      color: 'hover:text-blue-700' 
    },
    { 
      icon: <Instagram size={24} />, 
      href: 'https://www.instagram.com/369asma137/', 
      label: 'Instagram',
      color: 'hover:text-blue-400' 
    },
  ];

  const contactInfo = [
    {
      icon: <Mail size={24} className="text-sky-600" />,
      title: t('contact.info.email'),
      value: 'asmaessafi137@gmail.com',
      link: 'mailto:asmaessafi137@gmail.com',
    },
    {
      icon: <Phone size={24} className="text-sky-600" />,
      title: t('contact.info.phone'),
      value: '+216 29 551 459',
      link: 'tel:+21629551459',
    },
    {
      icon: <MapPin size={24} className="text-sky-600" />,
      title: t('contact.info.location'),
      value: 'Tunis, Tunisia',
      link: null,
    },
  ];

  return (
    <>
      <SEO
        title={t('contact.seo.title')}
        description={t('contact.seo.description')}
        keywords={['contact', 'get in touch', 'hire me', 'freelance']}
        ogUrl="/contact"
      />
      
      <div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12 mt-20"
        >
          <h1 className="text-4xl font-bold text-white mb-4 my-8">
            {t('contact.title')}
          </h1>
          <p className="text-lg text-primary-100 max-w-3xl">
            {t('contact.intro')}
          </p>
        </motion.div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <ContactForm />
          </div>
          
          <div className="lg:col-span-1 space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-opacity-15 bg-sky-800 rounded-xl shadow-md p-6"
            >
              <h3 className="text-xl font-semibold text-sky-600 mb-6">
                {t('contact.info.title')}
              </h3>
              
              <div className="space-y-6">
                {contactInfo.map((item, index) => (
                  <div key={index} className="flex items-start">
                    <div className="mt-1 mr-4">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-primary-300">{item.title}</h4>
                      {item.link ? (
                        <a
                          href={item.link}
                          className="text-white hover:text-sky-600 transition-colors"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-white">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-opacity-15 bg-sky-800 rounded-xl shadow-md p-6"
            >
              <h3 className="text-xl font-semibold text-sky-600 mb-6">
                {t('contact.social.title')}
              </h3>
              
              <div className="space-y-4">
                {socialLinks.map((link, index) => (
                  <a
                    key={index}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-primary-200 hover:text-sky-600 transition-colors"
                  >
                    <span className="mr-4">{link.icon}</span>
                    <span className="font-medium">{link.label}</span>
                  </a>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="bg-gradient-to-r from-sky-300 to-sky-700 rounded-xl shadow-md p-6 text-white"
            >
              <h3 className="text-xl font-semibold mb-4">
                {t('contact.availability.title')}
              </h3>
              <p className="text-primary-200 mb-3">
                {t('contact.availability.text')}
              </p>
              <p className="text-primary-200">
                {t('contact.availability.responseTime')}{' '}
                <span className="text-white font-medium">
                  {t('contact.availability.responseTimeValue')}
                </span>
              </p>
            </motion.div>
          </div>
        </div>
        
        {/* FAQ Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-16"
        >
          <h2 className="text-2xl font-bold text-white mb-8">
            {t('contact.faq.title')}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-opacity-15 bg-sky-800 rounded-xl shadow-md p-6">
              <h3 className="text-lg font-semibold text-sky-600 mb-3">
                {t('contact.faq.q1')}
              </h3>
              <p className="text-white">
                {t('contact.faq.a1')}
              </p>
            </div>
            
            <div className="bg-opacity-15 bg-sky-800 rounded-xl shadow-md p-6">
              <h3 className="text-lg font-semibold text-sky-600 mb-3">
                {t('contact.faq.q2')}
              </h3>
              <p className="text-white">
                {t('contact.faq.a2')}
              </p>
            </div>
            
            <div className="bg-opacity-15 bg-sky-800 rounded-xl shadow-md p-6">
              <h3 className="text-lg font-semibold text-sky-600 mb-3">
                {t('contact.faq.q3')}
              </h3>
              <p className="text-white">
                {t('contact.faq.a3')}
              </p>
            </div>
            
            <div className="bg-opacity-15 bg-sky-800 rounded-xl shadow-md p-6">
              <h3 className="text-lg font-semibold text-sky-600 mb-3">
                {t('contact.faq.q4')}
              </h3>
              <p className="text-white">
                {t('contact.faq.a4')}
              </p>
            </div>
          </div>
        </motion.section>
      </div>
    </>
  );
};

export default ContactPage;